
import React from 'react';
import { SEO } from '@/components/SEO';
import Navbar from '@/components/Navbar';
import Hero from '@/components/Hero';
import Services from '@/components/Services';
import Benefits from '@/components/Benefits';
import ContactForm from '@/components/ContactForm';
import MapSection from '@/components/MapSection';
import CallToAction from '@/components/CallToAction';
import CleaningGuideSection from '@/components/CleaningGuideSection';
import SeasonalServices from '@/components/SeasonalServices';
import HomeFAQ from '@/components/HomeFAQ';
import Footer from '@/components/Footer';
import { SERVICE_AREAS } from '@/data/serviceAreas';

const Index = () => {
  // LocalBusiness schema markup for local SEO
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Ventura Power Washing",
    "description": "Professional pressure washing, window cleaning, and carpet care services in Ventura County, California. Eco-friendly cleaning solutions for residential and commercial properties.",
    "url": "https://www.venturapowerwashing.com",
    "telephone": "(805) 464-7166",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Ventura County",
      "addressLocality": "Ventura",
      "addressRegion": "CA",
      "postalCode": "93001",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "34.2804",
      "longitude": "-119.2949"
    },
    "areaServed": SERVICE_AREAS.map(({ name }) => ({
      "@type": "City",
      "name": name,
      "containedInPlace": {
        "@type": "State",
        "name": "California"
      }
    })),
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Cleaning Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Pressure Washing",
            "description": "Professional high-pressure cleaning for driveways, decks, and exterior surfaces"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Window Cleaning",
            "description": "Streak-free window washing for residential and commercial properties"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Carpet Cleaning",
            "description": "Deep carpet cleaning and stain removal services"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Gutter Cleaning",
            "description": "Professional gutter cleaning and maintenance services"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Solar Panel Cleaning",
            "description": "Eco-friendly solar panel cleaning to maximize energy efficiency"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Soft Wash",
            "description": "Gentle cleaning for delicate exterior surfaces using low-pressure techniques"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Dumpster Pad Cleaning",
            "description": "Degreasing and sanitizing dumpster enclosures and pads for restaurants, HOAs, and commercial properties"
          }
        }
      ]
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "07:00",
      "closes": "19:00"
    },
    "image": "https://www.venturapowerwashing.com/og-image.jpg",
    "paymentAccepted": "Cash, Check, Credit Card",
    "priceRange": "$$"
  };

  // WebSite schema with sitelinks search box
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Ventura Power Washing",
    "url": "https://www.venturapowerwashing.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://www.venturapowerwashing.com/search?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  // Organization schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Ventura Power Washing",
    "url": "https://www.venturapowerwashing.com",
    "logo": "https://www.venturapowerwashing.com/logo/ventura-power-washing-icon.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "(805) 464-7166",
      "contactType": "Customer Service",
      "availableLanguage": "English",
      "areaServed": "US-CA"
    },
    "sameAs": [
      "https://www.facebook.com/venturapowerwashing",
      "https://www.instagram.com/venturacleaningco",
      "https://www.yelp.com/biz/ventura-power-washing-thousand-oaks"
    ]
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Ventura Power Washing | Professional Cleaning Services in Ventura County"
        description="Professional pressure washing, window cleaning, carpet care, and solar panel cleaning services in Ventura County, California. Eco-friendly solutions for residential and commercial properties. Free estimates!"
        path="/"
      >
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </SEO>
      <Navbar />
      <Hero />
      <Services />
      <Benefits />
      
      <SeasonalServices />
      <CallToAction />
      <MapSection />
      <HomeFAQ />
      <ContactForm />
      <Footer />
    </div>
  );
};

export default Index;
